import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchHolidayDestinations as fetchHolidayDestinationsApi,
  fetchHolidayFilters as fetchHolidayFiltersApi,
  fetchHolidayHome as fetchHolidayHomeApi,
  fetchHolidayPackageById as fetchHolidayPackageByIdApi,
  fetchHolidayPackages as fetchHolidayPackagesApi,
  fetchHolidaysList as fetchHolidaysListApi,
  fetchPackageDetailsBySlug as fetchPackageDetailsBySlugApi,
  fetchFeaturedJourney as fetchFeaturedJourneyApi,
  fetchUnlockParadise as fetchUnlockParadiseApi,
  searchHolidays as searchHolidaysApi,
} from "@/services/api/holidays";

const initialState = {
  home: null,
  featuredJourney: null,
  packages: [],
  listMeta: {
    totalCount: 0,
    pageIndex: 1,
    pageSize: 10,
    totalPages: 0,
  },
  packageDetail: null,
  detailLoading: false,
  detailError: null,
  filters: null,
  filtersRequestId: null,
  destinations: [],
  searchResults: [],
  loading: false,
  loadingMore: false,
  error: null,
};

export const fetchFeaturedJourney = createAsyncThunk(
  "holiday/fetchFeaturedJourney",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchFeaturedJourneyApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load pick your paradise");
    }
  }
);

export const fetchUnlockParadise = createAsyncThunk(
  "holiday/fetchUnlockParadise",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchUnlockParadiseApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load hidden paradise");
    }
  }
);

export const fetchHolidayHome = createAsyncThunk(
  "holiday/fetchHome",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchHolidayHomeApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load holiday home");
    }
  }
);

export const fetchHolidaysList = createAsyncThunk(
  "holiday/fetchList",
  async (arg, { rejectWithValue }) => {
    try {
      const { append = false, ...params } = arg || {};
      const response = await fetchHolidaysListApi(params);
      return { response, append };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load holidays list");
    }
  }
);

export const fetchHolidayPackages = createAsyncThunk(
  "holiday/fetchPackages",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchHolidayPackagesApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load holiday packages");
    }
  }
);

export const fetchHolidayPackageById = createAsyncThunk(
  "holiday/fetchPackageById",
  async (id, { rejectWithValue }) => {
    try {
      return await fetchHolidayPackageByIdApi(id);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load holiday package");
    }
  }
);

export const fetchPackageDetailsBySlug = createAsyncThunk(
  "holiday/fetchDetailsBySlug",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchPackageDetailsBySlugApi(params);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load package details"
      );
    }
  }
);

export const fetchHolidayFilters = createAsyncThunk(
  "holiday/fetchFilters",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchHolidayFiltersApi(params);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load holiday filters");
    }
  }
);

export const fetchHolidayDestinations = createAsyncThunk(
  "holiday/fetchDestinations",
  async (params, { rejectWithValue }) => {
    try {
      return await fetchHolidayDestinationsApi(params);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to load holiday destinations"
      );
    }
  }
);

export const fetchSearchHolidays = createAsyncThunk(
  "holiday/search",
  async (payload, { rejectWithValue }) => {
    try {
      return await searchHolidaysApi(payload);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to search holidays");
    }
  }
);

const holidaySlice = createSlice({
  name: "holiday",
  initialState,
  reducers: {
    clearHolidayError(state) {
      state.error = null;
    },
    resetHolidayState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeaturedJourney.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedJourney.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredJourney = action.payload.data;
      })
      .addCase(fetchFeaturedJourney.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUnlockParadise.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnlockParadise.fulfilled, (state, action) => {
        state.loading = false;
        state.unlockParadise = action.payload.data;
      })
      .addCase(fetchUnlockParadise.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHolidayHome.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHolidayHome.fulfilled, (state, action) => {
        state.loading = false;
        state.home = action.payload;
      })
      .addCase(fetchHolidayHome.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHolidaysList.pending, (state, action) => {
        if (action.meta.arg?.append) {
          state.loadingMore = true;
        } else {
          state.loading = true;
          state.loadingMore = false;
        }
        state.error = null;
      })
      .addCase(fetchHolidaysList.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        const payload = action.payload?.response?.data || action.payload?.response || {};
        const nextItems = payload.data || [];

        if (action.payload?.append) {
          const existingIds = new Set(state.packages.map((item) => item.packageId ?? item.id));
          const uniqueItems = nextItems.filter(
            (item) => !existingIds.has(item.packageId ?? item.id)
          );
          state.packages = [...state.packages, ...uniqueItems];
        } else {
          state.packages = nextItems;
        }

        state.listMeta = {
          totalCount: payload.totalCount || 0,
          pageIndex: payload.pageIndex || 1,
          pageSize: payload.pageSize || 10,
          totalPages: payload.totalPages || 0,
        };
      })
      .addCase(fetchHolidaysList.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload;
      })
      .addCase(fetchHolidayPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHolidayPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.packages = action.payload;
      })
      .addCase(fetchHolidayPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHolidayPackageById.fulfilled, (state, action) => {
        state.packageDetail = action.payload;
      })
      .addCase(fetchPackageDetailsBySlug.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchPackageDetailsBySlug.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.packageDetail = action.payload?.data || action.payload || null;
      })
      .addCase(fetchPackageDetailsBySlug.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
      })
      .addCase(fetchHolidayFilters.pending, (state, action) => {
        state.filters = null;
        state.filtersRequestId = action.meta.requestId;
      })
      .addCase(fetchHolidayFilters.fulfilled, (state, action) => {
        if (state.filtersRequestId && action.meta.requestId !== state.filtersRequestId) {
          return;
        }
        state.filters = action.payload?.data || action.payload || null;
      })
      .addCase(fetchHolidayFilters.rejected, (state, action) => {
        if (state.filtersRequestId && action.meta.requestId !== state.filtersRequestId) {
          return;
        }
        state.filters = null;
      })
      .addCase(fetchHolidayDestinations.fulfilled, (state, action) => {
        state.destinations = action.payload;
      })
      .addCase(fetchSearchHolidays.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearchHolidays.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload;
      })
      .addCase(fetchSearchHolidays.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearHolidayError, resetHolidayState } = holidaySlice.actions;
export default holidaySlice.reducer;
